import React, { useState } from "react";

import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import CustomInput from "../Form/Input";
import CustomButton from "../Form/CustomButtonWithHover";
import Dropdown from "../Form/Dropdown";
import ArrowDownIcon from "../../assets/icons/arrow-down.png";
import ProfileImageUploader from "../Form/ProfileImageUploader";

const UserProfile = ({ initialData = null, onSave, onCancel }) => {
  const [profilePicture, setProfilePicture] = useState(
    initialData?.profilePicture || null
  );
  const [selectedCountryCode, setSelectedCountryCode] = useState("+256");

  const customStyle = "w-full h-8 sm:h-11 text-[12px] sm:text-[16px] font-normal font-alexandria text-primary-text border border-[#B9C1CA] placeholder:font-inter placeholder:text-placeholder-text placeholder:text-[12px] sm:placeholder:text-[16px]";
  const headerStyles = "text-level-5 sm:text-xl font-medium text-blue mb-2 sm:mb-6";
  const labelStyles = " text-[12px] sm:text-[16px] font-alexandria font-medium text-primary-text mb-2 sm:mb-4";

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
  } = useForm(
    {
      fullName: initialData?.fullName || "",
      email: initialData?.email || "",
      phoneNumber: initialData?.phoneNumber || "",
      mobileMoneyProvider: initialData?.mobileMoneyProvider || "",
      mobileMoneyNumber: initialData?.mobileMoneyNumber || "",
      password: "",
      confirmPassword: "",
    },
    {
      fullName: true,
      email: true,
      phoneNumber: true,
      mobileMoneyProvider: true,
      mobileMoneyNumber: true,
      password: false,
      confirmPassword: false,
    },
    {
      fullName: COMMON_FIELD_TYPES.FULLNAME,
      email: COMMON_FIELD_TYPES.EMAIL_FIELD,
      phoneNumber: COMMON_FIELD_TYPES.PHONE,
      mobileMoneyProvider: COMMON_FIELD_TYPES.DROPDOWN,
      mobileMoneyNumber: COMMON_FIELD_TYPES.PHONE,
      password: COMMON_FIELD_TYPES.PASSWORD_FIELD,
      confirmPassword: COMMON_FIELD_TYPES.CONFIRM_PASSWORD,
    }
  );

  const mobileMoneyProviders = ["MTN", "Airtel "];

  const countryCodes = [{ code: "+256", country: "Uganda" }];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      fullName: true,
      email: true,
      phoneNumber: true,
      mobileMoneyProvider: true,
      mobileMoneyNumber: true,
      password: formData.password ? true : false,
      confirmPassword: formData.confirmPassword ? true : false,
    });

    if (validateForm()) {
      const updatedData = {
        ...formData,
        profilePicture,
        phoneNumber:
          selectedCountryCode + formData.phoneNumber.replace(/^\+\d+/, ""),
      };

      // Remove password fields if they're empty
      if (!formData.password) {
        delete updatedData.password;
        delete updatedData.confirmPassword;
      }

      await onSave(updatedData);
    }
  };

  const handleCancel = () => {
    setTouched({
      fullName: false,
      email: false,
      phoneNumber: false,
      mobileMoneyProvider: false,
      mobileMoneyNumber: false,
      password: formData.password ? false : false,
      confirmPassword: formData.confirmPassword ? false : false,
    });

    setFormData({
      fullName: initialData?.fullName || "",
      email: initialData?.email || "",
      phoneNumber: initialData?.phoneNumber || "",
      mobileMoneyProvider: initialData?.mobileMoneyProvider || null,
      mobileMoneyNumber: initialData?.mobileMoneyNumber || "",
      password: "",
      confirmPassword: "",
    });
    setProfilePicture(initialData?.profilePicture || null);

    onCancel();
  };

  return (
    <div className="w-full bg-background p-4 sm:p-8 rounded-lg">
      {/* Personal Information Section */}
      <div className="mb-2 sm:mb-8">
        <h2 className={headerStyles}>
          Personal Information
        </h2>

        {/* Profile Picture - Centered */}
        <div className="flex justify-center mb-2 sm:mb-4">
          <ProfileImageUploader
            isRequired={false}
            avatarContainerStyle="w-24 sm:w-32"
            imageStyle="w-24 h-24 sm:w-32 sm:h-32"
            labelStyle="font-alexandria font-medium text-primary-text"
            isDisabled={false} // or set to true if you want to disable
            loading={false} // set to true if uploading
            image={profilePicture}
            onImageUpload={setProfilePicture}
            selectedImage={profilePicture}
          />
        </div>

        {/* Full Name - Full Width */}
        <div className="mb-1 sm:mb-6">
          <CustomInput
            label="Full Name"
            labelStyle={labelStyles}
            isRequired={true}
            name="fullName"
            placeholder="Enter your full name"
            value={formData.fullName}
            error={touched.fullName && errors.fullName}
            onChange={(e) => handleInputChange("fullName", e.target.value)}
            onBlur={() => handleBlur("fullName", formData.fullName)}
            className={customStyle}
          />
        </div>

        {/* Email and Phone Number - Side by Side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 sm:gap-4 mb-1 sm:mb-8">
          <CustomInput
            label="Email"
            labelStyle={labelStyles}
            isRequired={true}
            name="email"
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            error={touched.email && errors.email}
            onChange={(e) => handleInputChange("email", e.target.value.toLowerCase())}
            onBlur={() => handleBlur("email", formData.email)}
            className={customStyle}          />

          <div>
            <label className={labelStyles }>
              Phone Number<span className="text-light-red ml-1">*</span>
            </label>
            <div className="flex mt-2 sm:mt-4">
              {/* Simple custom dropdown for country code */}
              <div className="w-[80px] sm:w-[100px] mr-2 relative ">
                <select
                  className="appearance-none w-full h-8 sm:h-11 text-[12px] sm:text-[16px] rounded-md bg-white border border-[#B9C1CA] text-placeholder-text font-alexandria  px-3 focus:outline-none"
                  value={selectedCountryCode}
                  onChange={(e) => setSelectedCountryCode(e.target.value)}
                >
                  {countryCodes.map((cc) => (
                    <option key={cc.code} value={cc.code}>
                      {cc.code}
                    </option>
                  ))}
                </select>
                <div className="pointer-events-none absolute top-4 right-3 sm:top-6 sm:right-4 transform -translate-y-1/2">
                  <img src={ArrowDownIcon} alt="arrow" className="w-4 h-4" />
                </div>
              </div>

              <div className="flex-1">
                <CustomInput
                  name="phoneNumber"
                  placeholder="Phone number"
                  value={formData.phoneNumber}
                  error={touched.phoneNumber && errors.phoneNumber}
                  onChange={(e) =>
                    handleInputChange("phoneNumber", e.target.value)
                  }
                  onBlur={() => handleBlur("phoneNumber", formData.phoneNumber)}
                  className={customStyle}                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Second Personal Information Section */}
      <div className="mb-2 sm:mb-8">
        <h2 className={headerStyles }>
          Personal Information
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
          <Dropdown
            label="Mobile Money Provider"
            labelStyle = {labelStyles}
            outerContainerStyle={"gap-0"}
            isRequired={true}
            name="mobileMoneyProvider"
            options={mobileMoneyProviders.map((provider) => ({
              name: provider,
              _id: provider,
            }))}
            placeholder="Select mobile money provider"
            buttonClassName="w-full h-8 sm:h-11 rounded-md bg-white text-primary-text font-alexandria text-[12px] sm:text-[16px] border border-[#B9C1CA]"
            defaultOption={
              formData.mobileMoneyProvider
                ? {
                    name: formData.mobileMoneyProvider,
                    _id: formData.mobileMoneyProvider,
                  }
                : null
            }
            error={touched.mobileMoneyProvider && errors.mobileMoneyProvider}
            onSelect={(option) =>
              handleInputChange(
                "mobileMoneyProvider",
                option ? option.name : ""
              )
            }
          />

          <CustomInput
            label="Mobile Money Number"
            labelStyle={labelStyles}
            isRequired={true}
            name="mobileMoneyNumber"
            placeholder="Enter mobile money number"
            value={formData.mobileMoneyNumber}
            error={touched.mobileMoneyNumber && errors.mobileMoneyNumber}
            onChange={(e) =>
              handleInputChange("mobileMoneyNumber", e.target.value)
            }
            onBlur={() =>
              handleBlur("mobileMoneyNumber", formData.mobileMoneyNumber)
            }
            className={customStyle}          />
        </div>
      </div>

      {/* Password Management Section */}
      <div className="mb-2 sm:mb-8">
        <h2 className={headerStyles}>
          Password Management
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 sm:gap-4">
          <div>

          <CustomInput
            label="New Password"
            labelStyle={labelStyles}
            isRequired={true}
            name="password"
            type="password"
            placeholder="Enter Password"
            value={formData.password}
            error={touched.password && errors.password}
            onChange={(e) => handleInputChange("password", e.target.value)}
            onBlur={() => handleBlur("password", formData.password)}
            className={customStyle}  />
            {touched.password && errors.password && (
                <div className={"h-8 sm:h-0"}></div>
            )}
          </div>

          <CustomInput
            label="Confirm Password"
            labelStyle={labelStyles}
            isRequired={true}
            name="confirmPassword"
            type="password"
            placeholder="Enter Password"
            value={formData.confirmPassword}
            error={touched.confirmPassword && errors.confirmPassword}
            onChange={(e) =>
              handleInputChange("confirmPassword", e.target.value)
            }
            onBlur={() =>
              handleBlur("confirmPassword", formData.confirmPassword)
            }
            className={customStyle}          />
        </div>
      </div>

      {/* Action Buttons - Centered */}
      <div className="flex justify-center gap-2 sm:gap-4">
        <CustomButton
          type="button"
          buttonText="Cancel"
          bgColor="bg-white"
          textColor="text-blue"
          className= " w-full md:w-[220px] h-10 sm:h-14 text-[12px] sm:text-[16px] font-alexandria font-semibold rounded-md border border-blue"
          onClick={handleCancel}
        />
        <CustomButton
          type="button"
          buttonText={`${initialData ? "Update" : "Save"} `}
          bgColor="bg-blue"
          textColor="text-white"
          className=" w-full md:w-[220px] h-10 sm:h-14 text-[12px] sm:text-[16px] font-alexandria font-semibold rounded-md"
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
};

export default UserProfile;
